import { useState, useCallback, forwardRef, useImperativeHandle, useMemo, useEffect, useRef } from 'react';
import useRouteManager from '../hooks/useRouteManager';

const NavigationManager = forwardRef(function NavigationManager(
    { map, userLocation, updateLoadingState, onRouteUpdate },
    ref
) {
    const [isNavigating, setIsNavigating] = useState(false);
    const [destination, setDestination] = useState(null);
    const lastUpdate = useRef(Date.now());
    const UPDATE_THRESHOLD = 5000; // 5 seconds

    const handleRouteUpdate = useCallback((routeInfo) => {
        console.log('🚗 [Navigation Manager Route Update]', {
            hasRouteInfo: !!routeInfo,
            nextManeuver: routeInfo?.nextManeuver,
            instruction: routeInfo?.instruction
        });
        
        if (onRouteUpdate) {
            onRouteUpdate(routeInfo);
        }
    }, [onRouteUpdate]);

    const routeManager = useRouteManager(map, handleRouteUpdate);

    const startNavigationTo = useCallback(async (destinationLocation) => {
        console.log('🎯 [Starting Navigation To]', destinationLocation);
        
        if (!userLocation || !map || !routeManager) {
            console.error('❌ [Navigation Error] Missing required dependencies', {
                hasLocation: !!userLocation,
                hasMap: !!map,
                hasRouteManager: !!routeManager
            });
            return;
        }

        updateLoadingState('route', true);
        try {
            setIsNavigating(true);
            setDestination(destinationLocation);
            
            console.log('🚗 [Creating Route]', {
                from: userLocation,
                to: destinationLocation
            });
            
            await routeManager.createRoute(userLocation, destinationLocation);
            lastUpdate.current = Date.now();
        } catch (error) {
            console.error('❌ [Navigation Error]', error);
            setIsNavigating(false);
            handleRouteUpdate(null);
        } finally {
            updateLoadingState('route', false);
        }
    }, [userLocation, map, routeManager, updateLoadingState, handleRouteUpdate]);

    const exitNavigation = useCallback(() => {
        console.log('🛑 [Exiting Navigation]');
        if (!routeManager) return;
        
        routeManager.clearRoute();
        handleRouteUpdate(null);
        setIsNavigating(false);
        setDestination(null);

        if (map && userLocation) {
            const region = new window.mapkit.CoordinateRegion(
                new window.mapkit.Coordinate(userLocation.latitude, userLocation.longitude),
                new window.mapkit.CoordinateSpan(0.005, 0.005)
            );
            map.region = region;
        }
    }, [routeManager, handleRouteUpdate, map, userLocation]);

    // Update route when location changes during navigation
    useEffect(() => {
        if (isNavigating && destination && userLocation && routeManager) {
            const now = Date.now();
            if (now - lastUpdate.current >= UPDATE_THRESHOLD) {
                console.log('🔄 [Updating Route]', {
                    from: userLocation,
                    to: destination,
                    timeSinceLastUpdate: now - lastUpdate.current
                });
                lastUpdate.current = now;
                
                // Update the route without force recalculation to prevent flickering
                // Only recalculate if explicitly needed (e.g., user deviated from route)
                routeManager.updateRoute(userLocation, destination, false).catch(error => {
                    console.error('❌ [Route Update Error]', error);
                });
            }
        }
    }, [isNavigating, destination, userLocation, routeManager]);

    useImperativeHandle(
        ref,
        () => ({
            startNavigationTo,
            exitNavigation,
            isNavigating
        }),
        [startNavigationTo, exitNavigation, isNavigating]
    );

    return null;
});

NavigationManager.displayName = 'NavigationManager';

export default NavigationManager; 